import { resolveLanguage, directionFor } from "./lib/languages";
import { LanguagePicker } from "./components/LanguagePicker";
import { localeFor } from "./lib/languages";
import { ui } from "./lib/localization";
import {
  Archive,
  ArrowUp,
  ArrowDown,
  Upload,
  AudioLines,
  Bell,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Cloud,
  Copy,
  Download,
  LoaderCircle,
  LogOut,
  MoreHorizontal,
  Moon,
  PanelLeft,
  Paperclip,
  Pause,
  Pencil,
  Plus,
  RefreshCw,
  Settings,
  SquarePen,
  Settings2,
  Square,
  Sun,
  Volume2,
  X,
} from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  getCapabilityLabel,
  getLocalizedError,
  getModelDescription,
  isBuiltInDefaultSystemPrompt,
  isDefaultConversationTitle,
  translations,
  type Language,
} from "./i18n";
import {
  CATALOG_SYNCED_AT,
  DEFAULT_MODEL_ID,
  FALLBACK_MODELS,
  formatContextWindow,
  formatPrice,
  getModel,
  supportsMultimodalAttachments,
} from "./lib/models";
import {
  clampOutputTokens,
  getOutputTokenPolicy,
  getOutputTokenPolicyForModel,
} from "./lib/output-tokens";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS_PER_MESSAGE,
  classifyFile,
} from "./lib/attachments";
import { prepareAttachment } from "./lib/prepare-attachment";
import { useChatGeneration } from "./lib/use-chat-generation";
import { loadWorkspace, loadConversation, hydrateWorkspace, getStorageUsage, cleanupStorage, saveWorkspace, WorkspaceStorageError } from "./lib/storage";
import { readPreference, writePreference } from "./lib/preferences";
import { workspaceCopy } from "./lib/workspace-copy";
import { planContext } from "./lib/context-budget";
import { cancelMessageEdit, forkConversationAt, startMessageEdit, updateDraft } from "./lib/conversation-edit";
import { createWorkspaceBackup, importWorkspaceBackup } from "./lib/workspace-backup";
import { hasRenderableMessageOutput } from "./lib/message-output";
import {
  getMessageContentForRequest,
  getRetainedImageContextForRequest,
  stripInternalImageContext,
} from "./lib/chat-context";
import { waitForImageJob } from "./lib/image-jobs";
import { getClientId } from "./lib/client-id";
import { recordAnonymousVisit } from "./lib/metrics";
import { recoverInterruptedMessage } from "./lib/workspace-recovery";
import { formatElapsedDuration, formatMessageTimestamp } from "./lib/time";
import {
  enableGenerationNotifications,
  isInstalledPwa,
  notifyGenerationComplete,
  readGenerationNotificationState,
  registerNeuronDeckServiceWorker,
  type BeforeInstallPromptEvent,
  type GenerationNotificationState,
} from "./lib/pwa";
import {
  prepareSpeechText,
  resolveSpeechRequest,
  resolveStoredSpeechMode,
  type SpeechMode,
} from "./lib/speech";
import {
  getSystemSpeechLocale,
  selectSystemSpeechVoice,
  splitSystemSpeechText,
  waitForSystemSpeechVoices,
} from "./lib/system-speech";
import {
  DEFAULT_IMAGE_MODEL_ID,
  getImageModel,
  IMAGE_MODELS,
  isImageModelId,
} from "./lib/image-models";
import {
  disconnectCloudflare,
  getCloudflareAuthStatus,
  selectCloudflareAccount,
  type CloudflareAuthStatus,
} from "./lib/cloudflare-auth";
import type { Attachment, ChatMessage, Conversation, ModelInfo, WorkspaceState } from "./types";
import { AttachmentStrip } from "./components/AttachmentStrip";
import { GeneratedImageGallery } from "./components/GeneratedImageGallery";
import { BrowserScreenshotGallery } from "./components/BrowserScreenshotGallery";
import { GeneratedFileGallery } from "./components/GeneratedFileGallery";
import { WebResearchPanel } from "./components/WebResearchPanel";
import { ModelPicker } from "./components/ModelPicker";
import { ConversationHistory, BranchNavigation } from "./components/ConversationHistory";
import { useMobileSidebar } from "./lib/use-mobile-sidebar";
import { useResourceCache } from "./lib/use-resource-cache";
import { formatFileSize } from "./lib/attachments";
import { getErrorAction } from "./lib/request-errors";
import {
  ChatGlyph,
  CodeGlyph,
  CreationGlyph,
  NeuronGlyph,
  UserGlyph,
} from "./components/ProductIcons";
import { ProviderLogo } from "./components/ProviderLogo";
import { isDialogBackdropClick, useModalDialog } from "./lib/use-modal-dialog";

const MarkdownMessage = lazy(() =>
  import("./components/MarkdownMessage").then((module) => ({ default: module.MarkdownMessage })),
);

const id = (): string => crypto.randomUUID();
const now = (): string => new Date().toISOString();
const MAX_CACHED_SPEECH_AUDIO = 12;
const normalizeComposerText = (value: string): string => value.replace(/\r\n?/g, "\n");
const insertComposerText = (editor: HTMLDivElement, value: string): string => {
  const selection = window.getSelection();
  if (!selection) return normalizeComposerText(editor.innerText);

  let range: Range;
  if (selection.rangeCount && editor.contains(selection.anchorNode)) {
    range = selection.getRangeAt(0);
    range.deleteContents();
  } else {
    range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
  }

  const textNode = document.createTextNode(normalizeComposerText(value));
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return normalizeComposerText(editor.innerText);
};
const LEGACY_VISION_MODEL_ID = "@cf/meta/llama-3.2-11b-vision-instruct";
const MAX_CONTEXT_ATTACHMENTS = 8;
const STARTER_ICONS = [CodeGlyph, CreationGlyph, ChatGlyph] as const;
const EMPTY_ATTACHMENTS: Attachment[] = [];

interface SpeechPlayback {
  messageId: string;
  status: "loading" | "playing" | "paused";
  source: "audio" | "system";
}

interface SystemSpeechSession {
  messageId: string;
  cancelled: boolean;
}

const pruneAttachmentsForRequest = (messages: ChatMessage[], modelId: string): ChatMessage[] => {
  let attachmentSlots = MAX_CONTEXT_ATTACHMENTS;
  let imageSlots = modelId === LEGACY_VISION_MODEL_ID ? 1 : MAX_ATTACHMENTS_PER_MESSAGE;

  return [...messages]
    .reverse()
    .map((message) => {
      if (!message.attachments?.length || attachmentSlots <= 0) {
        return { ...message, attachments: undefined };
      }

      const attachments: Attachment[] = [];
      for (const attachment of [...message.attachments].reverse()) {
        if (attachmentSlots <= 0) break;
        if (attachment.kind === "image") {
          if (imageSlots <= 0) continue;
          imageSlots -= 1;
        }
        attachments.unshift(attachment);
        attachmentSlots -= 1;
      }
      return { ...message, attachments: attachments.length ? attachments : undefined };
    })
    .reverse();
};

const prepareConversationContext = (conversation: Conversation, messages: ChatMessage[]) => planContext([
  ...(conversation.systemPrompt.trim() ? [{ role: "system" as const, content: conversation.systemPrompt.trim() }] : []),
  ...pruneAttachmentsForRequest(messages, conversation.modelId).filter((message) => message.content.trim()).map((message) => ({
    role: message.role, content: getMessageContentForRequest(message),
    ...(message.attachments?.length ? { attachments: message.attachments.filter((attachment) => attachment.kind !== "image" || getModel(FALLBACK_MODELS, conversation.modelId).capabilities.includes("vision")) } : {}),
    retainedImageContext: getRetainedImageContextForRequest(message),
  })),
], getModel(FALLBACK_MODELS, conversation.modelId).contextWindow, conversation.maxTokens);

const createConversation = (
  language: Language,
  modelId = DEFAULT_MODEL_ID,
  imageModelId = DEFAULT_IMAGE_MODEL_ID,
): Conversation => {
  const timestamp = now();
  const t = translations[language];
  const outputTokenPolicy = getOutputTokenPolicyForModel(modelId);
  return {
    id: id(),
    title: t.defaultConversation,
    modelId,
    systemPrompt: t.defaultSystemPrompt,
    temperature: 0.6,
    maxTokens: outputTokenPolicy.recommended,
    maxTokensCustomized: false,
    imageModelId,
    researchMode: false,
    messages: [],
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

const createWorkspace = (language: Language): WorkspaceState => {
  const conversation = createConversation(language);
  return {
    conversations: [conversation],
    activeConversationId: conversation.id,
    favoriteModelIds: ["@cf/zai-org/glm-4.7-flash", "@cf/openai/gpt-oss-120b"],
  };
};

const titleFromPrompt = (prompt: string): string => {
  const compact = prompt.replace(/\s+/g, " ").trim();
  return compact.length > 42 ? `${compact.slice(0, 42)}…` : compact;
};

function App() {
  const [language, setLanguage] = useState<Language>(
    () => resolveLanguage(readPreference("neurondeck-language")),
  );
  const t = { ...translations[language], ...workspaceCopy[language] };
  const [workspace, setWorkspace] = useState<WorkspaceState>(() => createWorkspace(language));
  const [models, setModels] = useState<ModelInfo[]>(FALLBACK_MODELS);
  const [catalogSyncedAt, setCatalogSyncedAt] = useState(CATALOG_SYNCED_AT);
  const [hydrated, setHydrated] = useState(false);
  const [storageState, setStorageState] = useState<"loading" | "saved" | "saving" | "error" | "conflict" | "load-error">("loading");
  const [conversationLoadError, setConversationLoadError] = useState<string>();
  const [trashedId, setTrashedId] = useState<string>();
  const [visibleMessageCount, setVisibleMessageCount] = useState(60);
  const prependHeightRef = useRef<number | undefined>(undefined);
  const [failedAttachments, setFailedAttachments] = useState<Array<{ id: string; conversationId: string; file: File; error: string }>>([]);
  const [storageUsage, setStorageUsage] = useState<string>();
  const [resourcePanelOpen, setResourcePanelOpen] = useState(false);
  const resourcePanelRef = useModalDialog(resourcePanelOpen);
  const [backupBusy, setBackupBusy] = useState(false);
  const [workspaceNotice, setWorkspaceNotice] = useState<string | null>(null);
  const [awayFromLatest, setAwayFromLatest] = useState(false);
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  const saveAllowedRef = useRef(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const messageScrollRef = useRef<HTMLDivElement | null>(null);
  const followLatestRef = useRef(true);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useMobileSidebar(sidebarOpen);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<"general" | "media" | "account">("general");
  const inspectorRef = useModalDialog(inspectorOpen);
  const [cloudflareAuth, setCloudflareAuth] = useState<CloudflareAuthStatus>({
    configured: false,
    authenticated: false,
    publicPoolConfigured: false,
    usesSiteQuota: false,
    accounts: [],
  });
  const [cloudflareAuthBusy, setCloudflareAuthBusy] = useState(true);
  const [cloudflareAuthError, setCloudflareAuthError] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [revealedTimeMessageId, setRevealedTimeMessageId] = useState<string | null>(null);
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (readPreference("neurondeck-theme-v2") as "dark" | "light" | null) ?? "light",
  );
  const [speechMode, setSpeechMode] = useState<SpeechMode>(
    () => resolveStoredSpeechMode(readPreference("neurondeck-speech-mode")),
  );
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [pwaInstalled, setPwaInstalled] = useState(() => isInstalledPwa());
  const [notificationState, setNotificationState] = useState<GenerationNotificationState>(
    () => readGenerationNotificationState(),
  );
  const [speechPlayback, setSpeechPlayback] = useState<SpeechPlayback | null>(null);
  const [speechError, setSpeechError] = useState<{ messageId: string; message: string } | null>(null);
  const speechAbortRef = useRef<AbortController | null>(null);
  const speechAudioRef = useRef<HTMLAudioElement | null>(null);
  const systemSpeechSessionRef = useRef<SystemSpeechSession | null>(null);
  const speechUrlsRef = useRef(new Map<string, string>());
  const composerEditorRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const resumedImageJobsRef = useRef(false);

  const activeConversation =
    workspace.conversations.find((conversation) => conversation.id === workspace.activeConversationId) ??
    workspace.conversations[0];
  const conversationUnavailable = activeConversation.messagesLoaded === false || Boolean(activeConversation.deletedAt);
  const generating = activeConversation.messages.some((message) => message.status === "streaming");
  const composer = activeConversation.draft?.content ?? "";
  const pendingAttachments = activeConversation.draft?.attachments ?? EMPTY_ATTACHMENTS;
  const editingMessageId = activeConversation.draft?.editingMessageId;
  const setComposer = useCallback((value: string) => {
    setWorkspace((current) => ({ ...current, conversations: current.conversations.map((conversation) =>
      conversation.id === activeConversation.id ? updateDraft(conversation, { content: value }) : conversation) }));
  }, [activeConversation.id]);
  const setPendingAttachments = useCallback((value: Attachment[] | ((current: Attachment[]) => Attachment[])) => {
    setWorkspace((current) => ({ ...current, conversations: current.conversations.map((conversation) =>
      conversation.id === activeConversation.id ? updateDraft(conversation, { attachments: typeof value === "function" ? value(conversation.draft?.attachments ?? []) : value }) : conversation) }));
  }, [activeConversation.id]);
  const activeModel = getModel(models, activeConversation.modelId);
  const activeModelSupportsAttachments = supportsMultimodalAttachments(activeModel);
  const activeModelSupportsTools = activeModel.capabilities.includes("tools");
  const researchModeAvailable = activeModelSupportsTools && cloudflareAuth.publicPoolConfigured;
  const activeImageModel = getImageModel(activeConversation.imageModelId);
  const activeOutputTokenPolicy = getOutputTokenPolicy(activeModel.contextWindow);
  const siteQuotaLabel = cloudflareAuth.publicPoolConfigured ? t.cloudflarePublicPool : t.cloudflareSiteQuota;
  const siteQuotaDescription = cloudflareAuth.publicPoolConfigured
    ? t.cloudflarePublicPoolDescription
    : t.cloudflareAccountDescription;

  const stopSpeechPlayback = useCallback(() => {
    speechAbortRef.current?.abort();
    speechAbortRef.current = null;
    speechAudioRef.current?.pause();
    speechAudioRef.current = null;
    if (systemSpeechSessionRef.current) systemSpeechSessionRef.current.cancelled = true;
    systemSpeechSessionRef.current = null;
    window.speechSynthesis?.cancel();
    setSpeechPlayback(null);
  }, []);

  useEffect(() => {
    writePreference("neurondeck-speech-mode", speechMode);
  }, [speechMode]);

  useEffect(() => {
    void registerNeuronDeckServiceWorker();
    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const handleInstalled = () => {
      setPwaInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  useEffect(() => {
    recordAnonymousVisit();
  }, []);

  useEffect(() => {
    stopSpeechPlayback();
    setSpeechError(null);
  }, [activeConversation.id, speechMode, stopSpeechPlayback]);

  useEffect(() => () => {
    speechAbortRef.current?.abort();
    speechAudioRef.current?.pause();
    if (systemSpeechSessionRef.current) systemSpeechSessionRef.current.cancelled = true;
    window.speechSynthesis?.cancel();
    for (const url of speechUrlsRef.current.values()) URL.revokeObjectURL(url);
    speechUrlsRef.current.clear();
  }, []);

  useEffect(() => {
    let active = true;
    const callbackStatus = new URL(window.location.href).searchParams.get("cloudflare");
    if (callbackStatus) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("cloudflare");
      window.history.replaceState({}, "", `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    }
    void getCloudflareAuthStatus()
      .then((status) => {
        if (!active) return;
        setCloudflareAuth(status);
        setCloudflareAuthError(callbackStatus === "error" || callbackStatus === "denied"
          ? t.cloudflareAuthFailed
          : status.error ?? null);
      })
      .catch((error) => {
        if (active) setCloudflareAuthError(error instanceof Error ? error.message : t.cloudflareAuthFailed);
      })
      .finally(() => {
        if (active) setCloudflareAuthBusy(false);
      });
    return () => {
      active = false;
    };
  }, [t.cloudflareAuthFailed]);

  useEffect(() => {
    if (!revealedTimeMessageId) return;
    const timeout = window.setTimeout(() => setRevealedTimeMessageId(null), 3_200);
    return () => window.clearTimeout(timeout);
  }, [revealedTimeMessageId]);

  useLayoutEffect(() => {
    const editor = composerEditorRef.current;
    if (!editor || editor.innerText === composer) return;
    editor.innerText = composer;
  }, [composer]);

  useEffect(() => {
    let active = true;
    void loadWorkspace({ lazy: true }).then((saved) => {
      if (!active) return;
      if (saved?.conversations?.length) {
        const validIds = new Set(FALLBACK_MODELS.map((model) => model.id));
        const conversations = saved.conversations.map((conversation) => {
          const modelId = validIds.has(conversation.modelId) ? conversation.modelId : DEFAULT_MODEL_ID;
          const outputTokenPolicy = getOutputTokenPolicyForModel(modelId);
          const hasStoredTokenValue = Number.isInteger(conversation.maxTokens) && conversation.maxTokens >= 64;
          const maxTokensCustomized = conversation.maxTokensCustomized ??
            (hasStoredTokenValue && conversation.maxTokens !== 2_048);
          return {
            ...conversation,
            messages: conversation.messages.map((message) => {
              const recovered = recoverInterruptedMessage(message, translations[language].generationInterrupted);
              return { ...recovered, content: stripInternalImageContext(recovered.content) };
            }),
            modelId,
            imageModelId: isImageModelId(conversation.imageModelId)
              ? conversation.imageModelId
              : DEFAULT_IMAGE_MODEL_ID,
            researchMode: conversation.researchMode === true,
            maxTokens: maxTokensCustomized
              ? clampOutputTokens(conversation.maxTokens, outputTokenPolicy)
              : outputTokenPolicy.recommended,
            maxTokensCustomized,
            systemPrompt:
              isBuiltInDefaultSystemPrompt(conversation.systemPrompt)
                ? translations[language].defaultSystemPrompt
                : conversation.systemPrompt,
          };
        });
        setWorkspace({ ...saved, conversations });
      }
      saveAllowedRef.current = true;
      setStorageState("saved");
      setHydrated(true);
    }).catch(() => {
      if (!active) return;
      saveAllowedRef.current = false;
      setStorageState("load-error");
      setHydrated(true);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setWorkspace((current) => ({
      ...current,
      conversations: current.conversations.map((conversation) => ({
        ...conversation,
        systemPrompt:
          isBuiltInDefaultSystemPrompt(conversation.systemPrompt)
            ? translations[language].defaultSystemPrompt
            : conversation.systemPrompt,
      })),
    }));
  }, [language]);

  const persistWorkspace = useCallback(async (state: WorkspaceState) => {
    if (!saveAllowedRef.current) return;
    setStorageState("saving");
    try {
      await saveWorkspace(state);
      if (workspaceRef.current === state) setStorageState("saved");
    } catch (error) {
      const conflict = error instanceof WorkspaceStorageError && error.code === "conflict";
      if (conflict) saveAllowedRef.current = false;
      setStorageState(conflict ? "conflict" : "error");
    }
  }, []);

  // A trailing save plus a periodic checkpoint prevents a fast stream starving persistence.
  useEffect(() => {
    if (!hydrated || !saveAllowedRef.current) return;
    const timeout = window.setTimeout(() => void persistWorkspace(workspace), 350);
    return () => window.clearTimeout(timeout);
  }, [workspace, hydrated, persistWorkspace]);

  useEffect(() => {
    if (!hydrated) return;
    let checkpoint = workspaceRef.current;
    const flush = () => {
      checkpoint = workspaceRef.current;
      void persistWorkspace(checkpoint);
    };
    const interval = window.setInterval(() => {
      if (checkpoint !== workspaceRef.current) flush();
    }, 2_000);
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [hydrated, persistWorkspace]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
      "content",
      theme === "dark" ? "#1c1c1e" : "#f5f5f7",
    );
    writePreference("neurondeck-theme-v2", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    document.title = `NeuronDeck · ${t.brandSubtitle}`;
    document.documentElement.dir = directionFor(language);
    writePreference("neurondeck-language", language);
  }, [language, t.htmlLang, t.brandSubtitle]);

  useEffect(() => {
    const catalogUrl = `/api/models?catalog=${encodeURIComponent(CATALOG_SYNCED_AT)}`;
    void fetch(catalogUrl, { cache: "no-store" })
      .then(async (response) => {
        if (!response.ok) throw new Error(t.modelCatalogUnavailable);
        return response.json() as Promise<{ models: ModelInfo[]; syncedAt: string }>;
      })
      .then((data) => {
        if (data.models.length) {
          setModels(data.models);
          setCatalogSyncedAt(data.syncedAt);
        }
      })
      .catch(() => {
        // The committed catalog keeps local development and transient outages usable.
      });
  }, [t.modelCatalogUnavailable]);

  const connectCloudflare = () => {
    setCloudflareAuthBusy(true);
    window.location.assign("/api/auth/cloudflare/start");
  };

  const installPwa = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setPwaInstalled(true);
    setInstallPrompt(null);
  };

  const enableNotifications = async () => {
    setNotificationState(await enableGenerationNotifications());
  };

  const changeCloudflareAccount = async (accountId: string) => {
    setCloudflareAuthBusy(true);
    setCloudflareAuthError(null);
    try {
      await selectCloudflareAccount(accountId);
      setCloudflareAuth(await getCloudflareAuthStatus());
    } catch (error) {
      setCloudflareAuthError(error instanceof Error ? error.message : t.cloudflareAuthFailed);
    } finally {
      setCloudflareAuthBusy(false);
    }
  };

  const logoutCloudflare = async () => {
    setCloudflareAuthBusy(true);
    setCloudflareAuthError(null);
    try {
      await disconnectCloudflare();
      setCloudflareAuth(await getCloudflareAuthStatus());
    } catch (error) {
      setCloudflareAuthError(error instanceof Error ? error.message : t.cloudflareAuthFailed);
    } finally {
      setCloudflareAuthBusy(false);
    }
  };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setModelPickerOpen(true);
      }
      if (event.key === "Escape" && !document.querySelector("dialog[open]")) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  const jumpToLatest = useCallback(() => {
    followLatestRef.current = true;
    setAwayFromLatest(false);
    const container = messageScrollRef.current;
    if (container) container.scrollTo({ top: container.scrollHeight, behavior: "instant" });
  }, []);

  useLayoutEffect(() => { setVisibleMessageCount(60); jumpToLatest(); }, [activeConversation.id, jumpToLatest]);
  useLayoutEffect(() => {
    const container = messageScrollRef.current;
    if (container && prependHeightRef.current != null) container.scrollTop += container.scrollHeight - prependHeightRef.current;
    prependHeightRef.current = undefined;
  }, [visibleMessageCount]);
  useLayoutEffect(() => {
    if (followLatestRef.current) jumpToLatest();
  }, [activeConversation.messages, generating, jumpToLatest]);

  useEffect(() => {
    const content = messageScrollRef.current?.firstElementChild;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => { if (followLatestRef.current) jumpToLatest(); });
    observer.observe(content);
    return () => observer.disconnect();
  }, [activeConversation.id, activeConversation.messages.length, jumpToLatest]);

  const updateConversation = useCallback((conversationId: string, update: (value: Conversation) => Conversation) => {
    setWorkspace((current) => ({
      ...current,
      conversations: current.conversations.map((conversation) =>
        conversation.id === conversationId ? update(conversation) : conversation,
      ),
    }));
  }, []);

  const updateMessage = useCallback(
    (conversationId: string, messageId: string, update: (value: ChatMessage) => ChatMessage) => {
      updateConversation(conversationId, (conversation) => ({
        ...conversation,
        updatedAt: now(),
        messages: conversation.messages.map((message) => (message.id === messageId ? update(message) : message)),
      }));
    },
    [updateConversation],
  );

  useEffect(() => {
    if (!hydrated || resumedImageJobsRef.current) return;
    resumedImageJobsRef.current = true;
    const controllers: AbortController[] = [];
    for (const conversation of workspace.conversations) {
      for (const message of conversation.messages) {
        const jobId = message.imageGeneration?.status === "generating"
          ? message.imageGeneration.jobId
          : undefined;
        if (!jobId || message.generatedImages?.length || message.generationSessionId) continue;
        const controller = new AbortController();
        controllers.push(controller);
        void waitForImageJob({
          jobId,
          clientId: getClientId(),
          signal: controller.signal,
          language,
        }).then((image) => {
          updateMessage(conversation.id, message.id, (current) => ({
            ...current,
            content: current.content || t.imageGeneratedFallback,
            generatedImages: current.generatedImages?.some((item) => item.id === image.id)
              ? current.generatedImages
              : [...(current.generatedImages ?? []), image],
            imageGeneration: {
              status: "complete",
              modelId: image.modelId,
              modelName: image.modelName,
              prompt: image.prompt,
            },
            status: "complete",
          }));
          void notifyGenerationComplete(language, "image");
        }).catch((error) => {
          if (controller.signal.aborted) return;
          updateMessage(conversation.id, message.id, (current) => ({
            ...current,
            content: current.content || (error instanceof Error ? error.message : t.generationFailed),
            imageGeneration: {
              ...current.imageGeneration!,
              status: "error",
              message: error instanceof Error ? error.message : t.generationFailed,
            },
            status: "error",
          }));
        });
      }
    }
    return () => controllers.forEach((controller) => controller.abort());
  }, [hydrated]);

  const updateCachedMessage = useCallback((conversationId: string, messageId: string, update: (message: ChatMessage) => ChatMessage) => {
    updateConversation(conversationId, (conversation) => ({ ...conversation, messages: conversation.messages.map((message) => message.id === messageId ? update(message) : message) }));
  }, [updateConversation]);
  const resourceCache = useResourceCache(workspace, hydrated, updateCachedMessage);
  const { generateResponse, stopGeneration: stopConversationGeneration, runningConversationIds, hasTask } = useChatGeneration({ language, updateMessage, prepareConversationContext });

  useEffect(() => {
    if (!hydrated) return;
    for (const conversation of workspace.conversations) {
      if (hasTask(conversation.id)) continue;
      const messageIndex = conversation.messages.findIndex((message) =>
        message.role === "assistant" && message.status === "streaming" && Boolean(message.generationSessionId));
      if (messageIndex < 0) continue;
      const message = conversation.messages[messageIndex];
      void generateResponse(conversation, conversation.messages.slice(0, messageIndex), message.id, message);
    }
  }, [generateResponse, hasTask, hydrated, workspace]);

  const sendMessage = useCallback(async () => {
    if (pendingAttachments.some((a) => a.kind === "image") && !activeModelSupportsAttachments) { setAttachmentError(t.imageNeedsVision); return; }
    const attachments = pendingAttachments;
    const prompt = composer.trim() || (attachments.length ? t.attachmentDefaultPrompt : "");
    if (!prompt || generating || attachmentBusy || !hydrated || conversationUnavailable) return;
    const editIndex = editingMessageId ? activeConversation.messages.findIndex((message) => message.id === editingMessageId) : -1;
    const target = editIndex >= 0
      ? forkConversationAt(activeConversation, editIndex, id(), t.branchTitle(activeConversation.title), now())
      : activeConversation;

    const timestamp = now();
    const userMessage: ChatMessage = {
      id: id(),
      role: "user",
      content: prompt,
      createdAt: timestamp,
      status: "complete",
      ...(attachments.length ? { attachments } : {}),
    };
    const assistantMessage: ChatMessage = {
      id: id(),
      role: "assistant",
      content: "",
      createdAt: timestamp,
      modelId: activeConversation.modelId,
      status: "streaming",
      streamCursor: 0,
    };
    assistantMessage.generationSessionId = assistantMessage.id;
    const contextMessages = [...target.messages, userMessage];
    const check = prepareConversationContext(target, contextMessages);
    if (check.error) { setAttachmentError(check.error === "context_system_too_large" ? t.systemTooLarge : t.contextTooLarge); return; }
    const snapshot: Conversation = { ...target, messages: contextMessages, draft: undefined };

    const nextConversation = { ...snapshot, archivedAt: undefined, messagesLoaded: true, title: target.messages.length === 0 && editIndex < 0 ? titleFromPrompt(prompt) : target.title,
      updatedAt: timestamp, messages: [...contextMessages, assistantMessage] };
    setWorkspace((current) => ({ ...current, activeConversationId: target.id,
      recentModelIds: [target.modelId, ...(current.recentModelIds ?? []).filter((value) => value !== target.modelId)].slice(0, 12),
      conversations: editIndex >= 0
        ? [nextConversation, ...current.conversations.map((conversation) => conversation.id === activeConversation.id ? cancelMessageEdit(conversation) : conversation)]
        : current.conversations.map((conversation) => conversation.id === target.id ? nextConversation : conversation),
    }));
    followLatestRef.current = true;
    setAttachmentError(null);
    await generateResponse(snapshot, contextMessages, assistantMessage.id);
  }, [
    activeConversation,
    activeModelSupportsAttachments,
    conversationUnavailable,
    attachmentBusy,
    composer,
    editingMessageId,
    hydrated,
    t.branchTitle,
    t.contextTooLarge,
    t.systemTooLarge,
    generateResponse,
    generating,
    pendingAttachments,
    t.attachmentDefaultPrompt,
    updateConversation,
  ]);

  const regenerate = useCallback(
    (messageId: string) => {
      if (generating) return;
      const index = activeConversation.messages.findIndex((message) => message.id === messageId);
      if (index < 0) return;
      const contextMessages = activeConversation.messages.slice(0, index);
      if (!contextMessages.some((message) => message.role === "user")) return;
      const assistantMessage: ChatMessage = {
        id: id(),
        role: "assistant",
        content: "",
        createdAt: now(),
        modelId: activeConversation.modelId,
        status: "streaming",
        streamCursor: 0,
      };
      assistantMessage.generationSessionId = assistantMessage.id;
      const branch = forkConversationAt(activeConversation, index, id(), t.branchTitle(activeConversation.title), now());
      const check = prepareConversationContext(branch, contextMessages);
      if (check.error) { setAttachmentError(check.error === "context_system_too_large" ? t.systemTooLarge : t.contextTooLarge); return; }
      setWorkspace((current) => ({ ...current, activeConversationId: branch.id, recentModelIds: [branch.modelId, ...(current.recentModelIds ?? []).filter((value) => value !== branch.modelId)].slice(0, 12), conversations: [
        { ...branch, messages: [...contextMessages, assistantMessage] }, ...current.conversations,
      ] }));
      void generateResponse(branch, contextMessages, assistantMessage.id);
    },
    [activeConversation, generateResponse, generating, t.branchTitle, t.contextTooLarge, t.systemTooLarge],
  );

  const stopGeneration = () => void stopConversationGeneration(activeConversation);

  const editMessage = (messageId: string) => {
    if (generating) return;
    const index = activeConversation.messages.findIndex((message) => message.id === messageId);
    const message = activeConversation.messages[index];
    if (!message || message.role !== "user") return;
    updateConversation(activeConversation.id, (conversation) => startMessageEdit(conversation, messageId));
    setAttachmentError(null);
    window.setTimeout(() => composerEditorRef.current?.focus(), 0);
  };

  const copyMessage = async (message: ChatMessage) => {
    await navigator.clipboard.writeText(message.content);
    setCopiedMessageId(message.id);
    window.setTimeout(() => setCopiedMessageId(null), 1400);
  };

  const toggleSpeech = useCallback(async (message: ChatMessage) => {
    if (speechPlayback?.messageId === message.id) {
      if (speechPlayback.status === "loading") return;
      if (speechPlayback.source === "system") {
        if (!("speechSynthesis" in window)) return;
        if (speechPlayback.status === "playing") {
          window.speechSynthesis.pause();
          setSpeechPlayback({ messageId: message.id, status: "paused", source: "system" });
        } else {
          window.speechSynthesis.resume();
          setSpeechPlayback({ messageId: message.id, status: "playing", source: "system" });
        }
        return;
      }
      const audio = speechAudioRef.current;
      if (!audio) return;
      if (speechPlayback.status === "playing") {
        audio.pause();
        setSpeechPlayback({ messageId: message.id, status: "paused", source: "audio" });
        return;
      }
      try {
        await audio.play();
        setSpeechPlayback({ messageId: message.id, status: "playing", source: "audio" });
      } catch {
        setSpeechPlayback({ messageId: message.id, status: "paused", source: "audio" });
      }
      return;
    }

    stopSpeechPlayback();
    setSpeechError(null);
    const text = prepareSpeechText(message.content);
    if (!text) {
      setSpeechError({ messageId: message.id, message: t.errors.invalid_tts_text });
      return;
    }

    const selection = resolveSpeechRequest(speechMode, text, language);
    if (selection.source === "system") {
      if (!("speechSynthesis" in window) || !("SpeechSynthesisUtterance" in window)) {
        setSpeechError({ messageId: message.id, message: t.systemSpeechUnsupported });
        return;
      }

      const session: SystemSpeechSession = { messageId: message.id, cancelled: false };
      systemSpeechSessionRef.current = session;
      setSpeechPlayback({ messageId: message.id, status: "loading", source: "system" });
      try {
        const voices = await waitForSystemSpeechVoices(window.speechSynthesis);
        if (session.cancelled || systemSpeechSessionRef.current !== session) return;
        const voice = selectSystemSpeechVoice(voices, selection.language);
        if (!voice) throw new Error(t.systemSpeechVoiceUnavailable);

        const chunks = splitSystemSpeechText(text);
        let chunkIndex = 0;
        const speakNext = () => {
          if (session.cancelled || systemSpeechSessionRef.current !== session) return;
          const chunk = chunks[chunkIndex];
          if (!chunk) {
            systemSpeechSessionRef.current = null;
            setSpeechPlayback(null);
            return;
          }
          chunkIndex += 1;
          const utterance = new SpeechSynthesisUtterance(chunk);
          utterance.lang = getSystemSpeechLocale(selection.language);
          utterance.voice = voice;
          utterance.rate = selection.language === "zh" ? 0.95 : 1;
          utterance.pitch = 1;
          utterance.onend = speakNext;
          utterance.onerror = (event) => {
            if (session.cancelled || systemSpeechSessionRef.current !== session || event.error === "canceled") return;
            systemSpeechSessionRef.current = null;
            setSpeechPlayback(null);
            setSpeechError({ messageId: message.id, message: t.speechFailed });
          };
          window.speechSynthesis.speak(utterance);
          setSpeechPlayback({ messageId: message.id, status: "playing", source: "system" });
        };
        speakNext();
      } catch (error) {
        if (session.cancelled || systemSpeechSessionRef.current !== session) return;
        systemSpeechSessionRef.current = null;
        setSpeechPlayback(null);
        setSpeechError({
          messageId: message.id,
          message: error instanceof Error ? error.message : t.speechFailed,
        });
      }
      return;
    }

    const cacheKey = `${message.id}:${speechMode}:${selection.model}:${selection.language}`;
    let audioUrl = speechUrlsRef.current.get(cacheKey);
    if (!audioUrl) {
      const controller = new AbortController();
      speechAbortRef.current = controller;
      setSpeechPlayback({ messageId: message.id, status: "loading", source: "audio" });
      try {
        const response = await fetch("/api/tts", {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "x-neurondeck-client": getClientId(),
            "accept-language": localeFor(language),
          },
          body: JSON.stringify({ text, model: selection.model, language: selection.language }),
          signal: controller.signal,
        });
        if (!response.ok) {
          const data = (await response.json().catch(() => null)) as
            | { error?: { code?: string; message?: string } }
            | null;
          throw new Error(getLocalizedError(
            language,
            data?.error?.code,
            data?.error?.message || t.speechFailed,
          ));
        }
        const audio = await response.blob();
        if (!audio.size || !audio.type.toLowerCase().startsWith("audio/")) throw new Error(t.speechFailed);
        audioUrl = URL.createObjectURL(audio);
        if (speechUrlsRef.current.size >= MAX_CACHED_SPEECH_AUDIO) {
          const oldest = speechUrlsRef.current.entries().next().value as [string, string] | undefined;
          if (oldest) {
            URL.revokeObjectURL(oldest[1]);
            speechUrlsRef.current.delete(oldest[0]);
          }
        }
        speechUrlsRef.current.set(cacheKey, audioUrl);
      } catch (error) {
        if (controller.signal.aborted) return;
        setSpeechPlayback(null);
        setSpeechError({
          messageId: message.id,
          message: error instanceof Error ? error.message : t.speechFailed,
        });
        return;
      } finally {
        if (speechAbortRef.current === controller) speechAbortRef.current = null;
      }
    }

    const audio = new Audio(audioUrl);
    speechAudioRef.current = audio;
    audio.onended = () => {
      if (speechAudioRef.current !== audio) return;
      speechAudioRef.current = null;
      setSpeechPlayback(null);
    };
    audio.onerror = () => {
      if (speechAudioRef.current !== audio) return;
      speechAudioRef.current = null;
      if (speechUrlsRef.current.get(cacheKey) === audioUrl) {
        speechUrlsRef.current.delete(cacheKey);
        URL.revokeObjectURL(audioUrl);
      }
      setSpeechPlayback(null);
      setSpeechError({ messageId: message.id, message: t.speechFailed });
    };
    try {
      await audio.play();
      setSpeechPlayback({ messageId: message.id, status: "playing", source: "audio" });
    } catch {
      setSpeechPlayback({ messageId: message.id, status: "paused", source: "audio" });
    }
  }, [
    language,
    speechMode,
    speechPlayback,
    stopSpeechPlayback,
    t.errors.invalid_tts_text,
    t.speechFailed,
    t.systemSpeechUnsupported,
    t.systemSpeechVoiceUnavailable,
  ]);

  const handleFileSelection = async (files: FileList | File[] | null, retryId?: string) => {
    if (!files?.length || attachmentBusy || generating || conversationUnavailable) return;
    const conversationId = activeConversation.id;
    setAttachmentBusy(true);
    setAttachmentError(null);
    if (retryId) setFailedAttachments((current) => current.filter((item) => item.id !== retryId));
    const availableSlots = Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - pendingAttachments.length);
    const selected = Array.from(files).slice(0, availableSlots);
    if (files.length > availableSlots) setAttachmentError(t.tooManyAttachments);
    let imageCount = pendingAttachments.filter((attachment) => attachment.kind === "image").length;
    const imageLimit = activeModel.id === LEGACY_VISION_MODEL_ID ? 1 : MAX_ATTACHMENTS_PER_MESSAGE;
    try {
      for (const file of selected) {
        try {
          if (classifyFile(file) === "image" && imageCount >= imageLimit) throw new Error(t.tooManyImages);
          const attachment = await prepareAttachment(file, { language, supportsImages: activeModelSupportsAttachments });
          if (attachment.kind === "image") imageCount += 1;
          // Commit each success immediately to its original conversation, even after navigating away.
          updateConversation(conversationId, (conversation) => updateDraft(conversation, {
            attachments: [...(conversation.draft?.attachments ?? []), attachment].slice(0, MAX_ATTACHMENTS_PER_MESSAGE),
          }));
        } catch (error) {
          setFailedAttachments((current) => [...current, { id: id(), conversationId, file, error: error instanceof Error ? error.message : t.attachmentReadFailed }]);
        }
      }
    } finally {
      setAttachmentBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingAttachment = (attachmentId: string) => {
    setPendingAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
    setAttachmentError(null);
  };

  const newConversation = () => {
    const conversation = createConversation(language, activeConversation.modelId, activeConversation.imageModelId);
    conversation.researchMode = activeConversation.researchMode === true;
    setWorkspace((current) => ({
      ...current,
      conversations: [conversation, ...current.conversations],
      activeConversationId: conversation.id,
    }));
    setSidebarOpen(false);
    setAttachmentError(null);
  };

  const selectConversation = useCallback(async (conversationId: string) => {
    setWorkspace((current) => ({ ...current, activeConversationId: conversationId }));
    setSidebarOpen(false);
    setAttachmentError(null);
    setConversationLoadError(undefined);
    const selected = workspaceRef.current.conversations.find((c) => c.id === conversationId);
    if (selected?.messagesLoaded !== false) return;
    try {
      const loaded = await loadConversation(conversationId);
      if (!loaded) throw new Error("conversation_missing");
      updateConversation(conversationId, (current) => current.messagesLoaded === false ? {
        ...loaded, ...current, messages: loaded.messages.map((message) => recoverInterruptedMessage(message, t.generationInterrupted)),
        draft: loaded.draft, messagesLoaded: true,
      } : current);
    } catch { setConversationLoadError(conversationId); }
  }, [updateConversation, t.generationInterrupted]);

  const deleteConversation = (conversationId: string, permanent = false) => {
    const conversation = workspaceRef.current.conversations.find((c) => c.id === conversationId);
    if (!conversation || hasTask(conversationId) || conversation.messages.some((m) => m.status === "streaming")) return;
    setWorkspace((current) => {
      let conversations = permanent ? current.conversations.filter((c) => c.id !== conversationId)
        : current.conversations.map((c) => c.id === conversationId ? { ...c, deletedAt: now() } : c);
      let activeId = current.activeConversationId;
      if (activeId === conversationId) {
        const remaining = conversations.find((c) => !c.deletedAt && !c.archivedAt && c.messagesLoaded !== false);
        if (remaining) activeId = remaining.id;
        else { const replacement = createConversation(language); conversations = [replacement, ...conversations]; activeId = replacement.id; }
      }
      return { ...current, conversations, activeConversationId: activeId };
    });
    if (!permanent) setTrashedId(conversationId);
  };

  const exportConversation = async (format: "markdown" | "html" = "markdown") => {
    if (activeConversation.messagesLoaded === false) return;
    setBackupBusy(true);
    try {
      const { exportConversationMarkdown, exportConversationHtml } = await import("./lib/conversation-export");
      const html = format === "html" ? await exportConversationHtml(activeConversation, { language, models }) : undefined;
      const text = html?.html ?? exportConversationMarkdown(activeConversation, { language, models });
      const url = URL.createObjectURL(new Blob([text], { type: format === "html" ? "text/html;charset=utf-8" : "text/markdown;charset=utf-8" }));
      const anchor = document.createElement("a"); anchor.href = url;
      anchor.download = `${activeConversation.title.replace(/[^\p{L}\p{N}]+/gu, "-").slice(0, 100) || "conversation"}.${format === "html" ? "html" : "md"}`;
      anchor.click(); window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
      setWorkspaceNotice(html?.unavailableAssets ? t.backupIncomplete(html.unavailableAssets) : ui(language, "Conversation exported"));
    } catch { setWorkspaceNotice(t.backupFailed); }
    finally { setBackupBusy(false); }
  };

  const refreshStorageUsage = useCallback(async () => {
    try {
      const usage = await getStorageUsage();
      setStorageUsage(`${formatFileSize(usage.usage)}${usage.quota ? ` / ${formatFileSize(usage.quota)}` : ""} · ${usage.assets} ${ui(language, "local resources")}`);
    } catch { setStorageUsage(ui(language, "Storage usage is unavailable")); }
  }, [language]);
  useEffect(() => {
    if (hydrated && ((inspectorOpen && settingsTab === "account") || resourcePanelOpen)) void refreshStorageUsage();
  }, [hydrated, inspectorOpen, settingsTab, resourcePanelOpen, storageState, refreshStorageUsage]);
  const cleanLocalStorage = async () => {
    try {
      const result = await cleanupStorage();
      setWorkspaceNotice(ui(language, "Removed {0} unused resources. Attachments in trash are retained.", result.removedAssets));
      await refreshStorageUsage();
    } catch { setWorkspaceNotice(ui(language, "Cleanup failed. Your saved history is unchanged.")); }
  };

  const selectModel = (modelId: string) => {
    const selectedModel = getModel(models, modelId);
    const outputTokenPolicy = getOutputTokenPolicy(selectedModel.contextWindow);
    updateConversation(activeConversation.id, (conversation) => ({
      ...conversation,
      modelId,
      maxTokens: conversation.maxTokensCustomized
        ? clampOutputTokens(conversation.maxTokens, outputTokenPolicy)
        : outputTokenPolicy.recommended,
      researchMode: selectedModel.capabilities.includes("tools") ? conversation.researchMode : false,
      updatedAt: now(),
    }));
    setWorkspace((current) => ({ ...current, recentModelIds: [modelId, ...(current.recentModelIds ?? []).filter((value) => value !== modelId)].slice(0, 12) }));
    setAttachmentError(!supportsMultimodalAttachments(selectedModel) && pendingAttachments.some((a) => a.kind === "image") ? t.imageNeedsVision : null);
    setModelPickerOpen(false);
  };

  const toggleFavorite = (modelId: string) => {
    setWorkspace((current) => ({
      ...current,
      favoriteModelIds: current.favoriteModelIds.includes(modelId)
        ? current.favoriteModelIds.filter((idValue) => idValue !== modelId)
        : [...current.favoriteModelIds, modelId],
    }));
  };

  const contextPreview = useMemo(() => {
    const editIndex = editingMessageId ? activeConversation.messages.findIndex((message) => message.id === editingMessageId) : -1;
    const messages = (editIndex >= 0 ? activeConversation.messages.slice(0, editIndex) : activeConversation.messages).filter((message) => message.status !== "streaming");
    return prepareConversationContext(activeConversation, [...messages,
      ...(composer.trim() || pendingAttachments.length ? [{ id: "draft", role: "user" as const, content: composer.trim() || t.attachmentDefaultPrompt, attachments: pendingAttachments, createdAt: now() }] : []),
    ]);
  }, [activeConversation.id, activeConversation.modelId, activeConversation.systemPrompt, activeConversation.maxTokens,
    activeConversation.messages.length, activeConversation.messages.at(-1)?.status, composer, editingMessageId, pendingAttachments, t.attachmentDefaultPrompt]);

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const { json, unavailableAssets } = await createWorkspaceBackup(await hydrateWorkspace(workspaceRef.current));
      const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
      const anchor = document.createElement("a");
      anchor.href = url; anchor.download = `neurondeck-backup-${new Date().toISOString().slice(0, 10)}.json`; anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
      setWorkspaceNotice(unavailableAssets ? t.backupIncomplete(unavailableAssets) : t.backupDone);
    } catch { setWorkspaceNotice(t.backupFailed); }
    finally { setBackupBusy(false); }
  };
  const restoreBackup = async (file: File) => {
    setBackupBusy(true);
    try {
      if (file.size > 100 * 1024 * 1024) throw new Error("backup_too_large");
      const imported = importWorkspaceBackup(await file.text(), t.generationInterrupted);
      setWorkspace((current) => ({ ...current, activeConversationId: imported.conversations[0].id, conversations: [...imported.conversations, ...current.conversations], favoriteModelIds: [...new Set([...current.favoriteModelIds, ...imported.favoriteModelIds])] }));
      setWorkspaceNotice(t.importDone(imported.conversations.length));
    } catch { setWorkspaceNotice(t.importFailed); }
    finally { setBackupBusy(false); }
  };

  const conversationTitle = (title: string) =>
    isDefaultConversationTitle(title)
      ? t.defaultConversation
      : title;

  return (
    <div className={`app-shell${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      {sidebarOpen && <button className="mobile-scrim" aria-label={t.closeSidebar} onClick={() => setSidebarOpen(false)} />}
      <aside ref={sidebarRef} className={sidebarOpen ? "sidebar open" : "sidebar"} role={sidebarOpen ? "dialog" : undefined} aria-modal={sidebarOpen || undefined} aria-label={t.conversations}>
        <div className="brand-row">
          <div className="brand-mark"><NeuronGlyph /></div>
          <div className="brand-copy">
            <strong>NeuronDeck</strong>
            <span>{t.brandSubtitle}</span>
          </div>
          <button className="icon-button sidebar-close" onClick={() => setSidebarOpen(false)} aria-label={t.closeSidebar}>
            <X size={18} />
          </button>
        </div>

        <button className="new-chat-button" onClick={newConversation} type="button">
          <SquarePen size={18} />
          {t.newConversation}
          <Plus size={15} className="new-chat-plus" aria-hidden="true" />
        </button>

        <ConversationHistory conversations={workspace.conversations} activeId={activeConversation.id} models={models} language={language}
          runningIds={runningConversationIds} onSelect={(id) => void selectConversation(id)} title={conversationTitle}
          onUpdate={(id, patch) => updateConversation(id, (conversation) => ({ ...conversation, ...patch }))}
          onTrash={(id) => deleteConversation(id)} onDelete={(id) => deleteConversation(id, true)} />
        {trashedId ? <div className="trash-notice" role="status"><span>{ui(language, "Moved to trash")}</span><button type="button" onClick={() => { updateConversation(trashedId, (c) => ({ ...c, deletedAt: undefined })); setTrashedId(undefined); }}>{ui(language, "Undo")}</button><button type="button" aria-label={ui(language, "Dismiss")} onClick={() => setTrashedId(undefined)}><X size={13} /></button></div> : null}

        <div className="sidebar-footer">
          <div className="storage-card">
            <Archive size={16} />
            <div><strong>{t.localFirst}</strong><span>{storageState === "saving" ? t.saving : storageState === "saved" ? t.saved : t.localFirstDetail}</span></div>
          </div>
          <button className="sidebar-action" onClick={() => void exportConversation()} disabled={backupBusy || conversationUnavailable} type="button">
            <Download size={16} />{t.exportConversation}
          </button>
          <button className="sidebar-action" type="button" onClick={() => { setSidebarOpen(false); setResourcePanelOpen(true); }} disabled={activeConversation.messagesLoaded === false}><Archive size={16} />{ui(language, "Resources and offline export")}</button>
          <a className="sidebar-action" href={language === "zh" ? "/zh/cloudflare-workers-ai-chat" : "/en/cloudflare-workers-ai-chat"}>
            <BookOpen size={16} />{t.productGuide}
          </a>
          <nav className="sidebar-legal" aria-label={ui(language, "Site information")}>
            <a href="/privacy.html">{t.privacyPolicy}</a>
            <span aria-hidden="true">·</span>
            <a href="/terms.html">{t.termsOfUse}</a>
          </nav>
        </div>
      </aside>

      <main className="main-panel">
        <header className="topbar">
          <button className="icon-button sidebar-toggle" onClick={() => {
            if (window.matchMedia("(max-width: 780px)").matches) setSidebarOpen(true);
            else setSidebarCollapsed((current) => !current);
          }} aria-label={sidebarCollapsed ? t.openSidebar : t.toggleSidebar} title={t.toggleSidebar}>
            <PanelLeft size={20} />
          </button>
          <button className="model-trigger" onClick={() => setModelPickerOpen(true)} type="button">
            <span className="active-model-mark"><ProviderLogo provider={activeModel.provider} /></span>
            <span><small>{activeModel.provider}</small><strong>{activeModel.name}</strong></span>
            <ChevronDown size={16} />
          </button>
          <div className="topbar-actions">
            <button
              className={cloudflareAuth.authenticated ? "cloud-account-button connected desktop-toolbar-action" : "cloud-account-button desktop-toolbar-action"}
              onClick={() => { setSettingsTab("account"); setInspectorOpen(true); }}
              aria-label={t.cloudflareAccount}
              title={cloudflareAuth.authenticated && !cloudflareAuth.usesSiteQuota ? cloudflareAuth.activeAccountName : siteQuotaLabel}
              type="button"
            >
              <Cloud size={17} />
              <span>{cloudflareAuth.authenticated && !cloudflareAuth.usesSiteQuota ? cloudflareAuth.activeAccountName : siteQuotaLabel}</span>
            </button>
            <LanguagePicker language={language} onChange={setLanguage} className="desktop-toolbar-action" />
            <button
              className="icon-button desktop-toolbar-action"
              onClick={() => setTheme((current) => (current === "dark" ? "light" : "dark"))}
              aria-label={t.toggleTheme}
              type="button"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              className={inspectorOpen ? "icon-button active" : "icon-button"}
              onClick={() => setInspectorOpen((current) => !current)}
              aria-label={t.toggleInspector}
              type="button"
            >
              <Settings size={19} />
            </button>
          </div>
        </header>

        <div className="workspace-row">
          <section className={`chat-panel${activeConversation.messages.length === 0 && !conversationUnavailable && hydrated ? " is-empty" : ""}`}>
            <BranchNavigation conversation={activeConversation} conversations={workspace.conversations} onSelect={(id) => void selectConversation(id)} language={language} />
            {activeConversation.deletedAt ? <div className="editing-banner"><span>{ui(language, "This conversation is in trash")}</span><button type="button" onClick={() => updateConversation(activeConversation.id, (c) => ({ ...c, deletedAt: undefined, archivedAt: undefined }))}>{ui(language, "Restore chat")}</button></div> : null}
            <div className="message-scroll" ref={messageScrollRef} onScroll={() => {
              const container = messageScrollRef.current;
              if (!container) return;
              const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
              followLatestRef.current = nearBottom;
              setAwayFromLatest(!nearBottom);
            }}>
              {!hydrated || activeConversation.messagesLoaded === false ? <div className="history-loading" role="status">{conversationLoadError === activeConversation.id ? <><p>{ui(language, "Could not load this conversation. It is still saved locally.")}</p><button type="button" onClick={() => void selectConversation(activeConversation.id)}>{ui(language, "Retry")}</button></> : <><LoaderCircle className="spinning" size={22} /><p>{ui(language, "Loading conversation…")}</p></>}</div> : activeConversation.messages.length === 0 ? (
                <div className="welcome-state">
                  <div className="welcome-orbit" aria-hidden="true"><NeuronGlyph /></div>
                  <span className="eyebrow">{t.welcomeEyebrow}</span>
                  <h1>{t.welcomeTitle}</h1>
                  <p>{t.welcomeDescription}</p>
                </div>
              ) : (
                <div className="messages">
                  {activeConversation.messages.length > visibleMessageCount ? <button className="load-earlier-button" type="button" onClick={() => { followLatestRef.current = false; prependHeightRef.current = messageScrollRef.current?.scrollHeight; setVisibleMessageCount((count) => count + 60); }}>{ui(language, "Load earlier messages ({0} remaining)", activeConversation.messages.length - visibleMessageCount)}</button> : null}
                  {activeConversation.messages.slice(-visibleMessageCount).map((message) => (
                    <article className={`message ${message.role}`} key={message.id}>
                      <div className="message-identity">
                        {message.role === "user" ? (
                          <div className="avatar user-avatar"><UserGlyph /></div>
                        ) : (
                          <div className="avatar ai-avatar"><ProviderLogo provider={getModel(models, message.modelId ?? activeModel.id).provider} /></div>
                        )}
                        <div>
                          <strong>{message.role === "user" ? t.you : getModel(models, message.modelId ?? activeModel.id).name}</strong>
                          <span>{message.elapsedMs != null
                            ? formatElapsedDuration(message.elapsedMs, language)
                            : message.status === "streaming"
                              ? message.recoveryState === "recovering" ? t.recovering : t.generating
                              : ""}</span>
                        </div>
                      </div>
                      <div className="message-content">
                        {message.attachments?.length ? (
                          <AttachmentStrip attachments={message.attachments} language={language} />
                        ) : null}
                        <GeneratedImageGallery
                          images={message.generatedImages}
                          messageCreatedAt={message.createdAt}
                          state={message.imageGeneration}
                          language={language}
                        />
                        <BrowserScreenshotGallery
                          screenshots={message.browserScreenshots}
                          messageCreatedAt={message.createdAt}
                          language={language}
                        />
                        <GeneratedFileGallery files={message.generatedFiles} language={language} />
                        <WebResearchPanel
                          state={message.webResearch}
                          sources={message.webSources}
                          language={language}
                          reportTitle={activeConversation.title}
                          reportContent={message.content}
                          settled={message.status !== "streaming"}
                        />
                        {message.reasoning && (
                          <details className="reasoning-block" open={message.status === "streaming"}>
                            <summary>{t.reasoningTrace}</summary>
                            <div className="reasoning-markdown">
                              <Suspense fallback={<p>{message.reasoning}</p>}>
                                <MarkdownMessage content={message.reasoning} language={language} />
                              </Suspense>
                            </div>
                          </details>
                        )}
                        {message.content ? (
                          <Suspense fallback={<p>{message.content}</p>}>
                            <MarkdownMessage content={message.content} language={language} sources={message.webSources} />
                          </Suspense>
                        ) : message.status === "error" || message.imageGeneration || message.generatedImages?.length || message.browserScreenshots?.length || message.generatedFiles?.length || message.webResearch ? null : (
                          <div className="typing"><span /><span /><span /></div>
                        )}
                        {message.error ? <div className="message-error" role="alert"><p>{getLocalizedError(language, message.errorCode, ui(language, message.error))}</p>{message.retryAt ? <p>{ui(language, "Retry after: ")}{new Date(message.retryAt).toLocaleTimeString(localeFor(language))}</p> : null}{message.status === "error" || getErrorAction(message.errorCode) === "stop" ? <button type="button" onClick={() => {
                          const action = getErrorAction(message.errorCode);
                          if (action === "stop") void stopConversationGeneration(activeConversation);
                          else if (action === "account") { setSettingsTab("account"); setInspectorOpen(true); }
                          else if (action === "model") setModelPickerOpen(true);
                          else if (action === "reconnect" && message.generationSessionId) {
                            updateMessage(activeConversation.id, message.id, (current) => ({ ...current, status: "streaming", error: undefined, recoveryState: "recovering" }));
                            void generateResponse(activeConversation, activeConversation.messages.slice(0, activeConversation.messages.findIndex((m) => m.id === message.id)), message.id, message);
                          } else regenerate(message.id);
                        }}>{getErrorAction(message.errorCode) === "stop" ? ui(language, "Retry stopping") : getErrorAction(message.errorCode) === "account" ? ui(language, "Account and quota") : getErrorAction(message.errorCode) === "model" ? ui(language, "Choose another model") : getErrorAction(message.errorCode) === "reconnect" ? ui(language, "Reconnect task") : ui(language, "Retry")}</button> : null}</div> : null}
                      </div>
                      {(hasRenderableMessageOutput(message) || message.status === "error") && message.status !== "streaming" && (
                        <div className="message-actions">
                          <button onClick={() => void copyMessage(message)} type="button">
                            {copiedMessageId === message.id ? <Check size={14} /> : <Copy size={14} />}
                            {copiedMessageId === message.id ? t.copied : t.copy}
                          </button>
                          {message.role === "assistant" ? (
                            <>
                              {message.content ? (
                                <button
                                  className={speechPlayback?.messageId === message.id ? "speech-action active" : "speech-action"}
                                  onClick={() => void toggleSpeech(message)}
                                  disabled={speechPlayback?.messageId === message.id && speechPlayback.status === "loading"}
                                  type="button"
                                  aria-pressed={speechPlayback?.messageId === message.id && speechPlayback.status === "playing"}
                                >
                                  {speechPlayback?.messageId === message.id && speechPlayback.status === "loading"
                                    ? <LoaderCircle className="spinning" size={14} />
                                    : speechPlayback?.messageId === message.id && speechPlayback.status === "playing"
                                      ? <Pause size={14} />
                                      : <Volume2 size={14} />}
                                  {speechPlayback?.messageId === message.id && speechPlayback.status === "loading"
                                    ? t.preparingSpeech
                                    : speechPlayback?.messageId === message.id && speechPlayback.status === "playing"
                                      ? t.pauseSpeech
                                      : speechPlayback?.messageId === message.id && speechPlayback.status === "paused"
                                        ? t.resumeSpeech
                                        : t.readAloud}
                                </button>
                              ) : null}
                              <button onClick={() => regenerate(message.id)} type="button"><RefreshCw size={14} />{t.regenerate}</button>
                            </>
                          ) : (
                            <button onClick={() => editMessage(message.id)} type="button"><Pencil size={14} />{t.editFromHere}</button>
                          )}
                          <button
                            className="message-time-toggle"
                            type="button"
                            aria-label={t.showMessageTime}
                            aria-expanded={revealedTimeMessageId === message.id}
                            onClick={() => setRevealedTimeMessageId((current) => current === message.id ? null : message.id)}
                          ><MoreHorizontal size={14} /></button>
                          <time
                            className={revealedTimeMessageId === message.id ? "message-time revealed" : "message-time"}
                            dateTime={message.createdAt}
                            title={new Date(message.createdAt).toLocaleString(localeFor(language))}
                          >{formatMessageTimestamp(message.createdAt, language)}</time>
                          {speechError?.messageId === message.id ? (
                            <span className="speech-error" role="alert" title={speechError.message}>{speechError.message}</span>
                          ) : null}
                        </div>
                      )}
                    </article>
                  ))}
                  <div ref={messageEndRef} />
                </div>
              )}
            </div>

            <div className="composer-wrap">
              {awayFromLatest ? <button className="jump-to-latest" type="button" onClick={jumpToLatest}><ArrowDown size={15} />{t.jumpToLatest}</button> : null}
              {(["error", "load-error", "conflict"] as string[]).includes(storageState) ? <div className="storage-alert" role="alert">
                <span>{storageState === "load-error" ? t.loadFailed : storageState === "conflict" ? t.saveConflict : t.saveFailed}</span>
                <button type="button" onClick={() => void downloadBackup()} disabled={backupBusy}>{t.backupWorkspace}</button>
                {storageState === "error" ? <button type="button" onClick={() => void persistWorkspace(workspaceRef.current)}>{t.retrySave}</button> : <button type="button" onClick={() => window.location.reload()}>{t.reloadWorkspace}</button>}
              </div> : null}
              {editingMessageId ? <div className="editing-banner"><span>{t.editingDraft}</span><button type="button" onClick={() => updateConversation(activeConversation.id, cancelMessageEdit)}>{t.cancelEdit}</button></div> : null}
              <div className="composer">
                <>
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ATTACHMENT_ACCEPT}
                      multiple
                      hidden
                      onChange={(event) => void handleFileSelection(event.target.files)}
                    />
                    {pendingAttachments.length ? (
                      <AttachmentStrip
                        attachments={pendingAttachments}
                        language={language}
                        onRemove={removePendingAttachment}
                        pending
                      />
                    ) : null}
                  </>
                </>
                <div
                  id="chat-composer"
                  ref={composerEditorRef}
                  className="composer-editor"
                      dir="auto"
                  role="textbox"
                  aria-multiline="true"
                  contentEditable={!generating && hydrated && !conversationUnavailable}
                  suppressContentEditableWarning
                  tabIndex={0}
                  onInput={(event) => setComposer(normalizeComposerText(event.currentTarget.innerText))}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
                    event.preventDefault();
                    if (event.shiftKey) {
                      setComposer(insertComposerText(event.currentTarget, "\n"));
                      return;
                    }
                    void sendMessage();
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                    setComposer(insertComposerText(event.currentTarget, event.clipboardData.getData("text/plain")));
                  }}
                  data-placeholder={t.messageModel(activeModel.name)}
                  aria-label={t.messageAria}
                  autoCapitalize="sentences"
                  autoCorrect="on"
                  inputMode="text"
                  enterKeyHint="send"
                  spellCheck
                  aria-disabled={generating}
                />
                <div className="composer-bottom">
                  <div className="composer-tools">
                    <>
                      <button
                        className="attach-button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={conversationUnavailable || generating || attachmentBusy || pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
                        type="button"
                        aria-label={t.attachFiles}
                        title={t.attachFiles}
                      >
                        {attachmentBusy ? <LoaderCircle className="spinning" size={17} /> : <Paperclip size={17} />}
                      </button>
                    </>
                    <button
                      className={activeConversation.researchMode ? "research-mode-button active" : "research-mode-button"}
                      type="button"
                      aria-pressed={activeConversation.researchMode === true}
                      disabled={generating || conversationUnavailable}
                      aria-disabled={!activeConversation.researchMode && !researchModeAvailable}
                      title={activeConversation.researchMode
                        ? t.researchModeDescription
                        : !activeModelSupportsTools
                          ? t.researchModeNeedsTools
                          : !cloudflareAuth.publicPoolConfigured
                            ? t.researchModeNeedsBrowser
                            : t.researchModeDescription}
                      onClick={() => {
                        if (!activeConversation.researchMode && !researchModeAvailable) {
                          setAttachmentError(!activeModelSupportsTools ? t.researchModeNeedsTools : t.researchModeNeedsBrowser);
                          if (!activeModelSupportsTools) setModelPickerOpen(true);
                          return;
                        }
                        updateConversation(activeConversation.id, (conversation) => ({ ...conversation, researchMode: !conversation.researchMode, updatedAt: now() }));
                      }}
                    >
                      <BookOpen size={16} />
                      <span>{activeConversation.researchMode ? t.researchModeActive : t.researchMode}</span>
                    </button>
                    <div className="composer-badges">
                      <button onClick={() => setModelPickerOpen(true)} type="button"><span className="active-model-mark"><ProviderLogo provider={activeModel.provider} /></span><span className="composer-model-name">{activeModel.name}</span></button>
                      {activeModelSupportsTools ? (
                        <span className="image-tool-badge"><CreationGlyph />{t.imageToolBadge(activeImageModel.name)}</span>
                      ) : null}
                      <span>{formatContextWindow(activeModel.contextWindow, language)}</span>
                    </div>
                  </div>
                  {generating ? (
                    <button className="send-button stop" onClick={stopGeneration} type="button" aria-label={t.stopGenerating}><Square size={15} fill="currentColor" /></button>
                  ) : (
                    <button
                      className="send-button"
                      onClick={() => void sendMessage()}
                      disabled={
                        (!composer.trim() && !pendingAttachments.length) || conversationUnavailable || attachmentBusy || !hydrated || Boolean(contextPreview.error)
                      }
                      type="button"
                      aria-label={t.sendMessage}
                    ><ArrowUp size={21} /></button>
                  )}
                </div>
              </div>
              {resourceCache.failed ? <button className="resource-cache-notice" type="button" onClick={() => setResourcePanelOpen(true)}>{ui(language, "Some resources are not saved locally. View details")}</button> : null}
              {attachmentBusy ? <p className="attachment-status">{t.processingAttachment}</p> : null}
              {attachmentError ? <p className="attachment-error" role="alert">{attachmentError}</p> : null}
              {failedAttachments.filter((item) => item.conversationId === activeConversation.id).map((item) => <div className="attachment-failure" key={item.id} role="alert"><span><strong>{item.file.name}</strong> · {item.error}</span><button type="button" disabled={attachmentBusy || generating || pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE} onClick={() => void handleFileSelection([item.file], item.id)}>{ui(language, "Retry")}</button><button type="button" onClick={() => setFailedAttachments((current) => current.filter((failed) => failed.id !== item.id))} aria-label={ui(language, "Dismiss failed attachment")}><X size={14} /></button></div>)}
              <div className="context-status" title={t.contextDetails} hidden={!composer.trim() && !pendingAttachments.length && activeConversation.messages.length === 0 && !contextPreview.error}>
                <span>{t.contextUsage(contextPreview.estimatedTokens.toLocaleString(language), contextPreview.inputBudget.toLocaleString(language))}</span>
                {contextPreview.summarizedMessages ? <span>{t.contextCompacted(contextPreview.summarizedMessages)}</span> : null}
              </div>
              {contextPreview.error ? <p className="attachment-error" role="alert">{contextPreview.error === "context_system_too_large" ? t.systemTooLarge : t.contextTooLarge}</p> : null}
              <p className="composer-note">{t.modelCaution}</p>
            </div>
            {activeConversation.messages.length === 0 && !conversationUnavailable && hydrated ? (
              <div className="starter-grid">
                {t.starterPrompts.map((item, index) => {
                  const StarterIcon = STARTER_ICONS[index % STARTER_ICONS.length];
                  return (
                    <button className={`starter-${index}`} key={item.label} type="button" onClick={() => {
                      setComposer(item.prompt);
                      if (index === 1 && !activeModelSupportsTools) {
                        setAttachmentError(ui(language, "Image generation requires a model with tools. Select a model labeled Tools."));
                        setModelPickerOpen(true);
                      }
                      composerEditorRef.current?.focus();
                    }}>
                      <span className="starter-icon"><StarterIcon /></span>
                      <span className="starter-copy"><strong>{item.label}</strong><span className="starter-prompt">{item.description}</span></span>
                      <ChevronRight className="starter-arrow" size={15} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            ) : null}
          </section>

          <dialog ref={resourcePanelRef} className="resource-dialog" aria-label={ui(language, "Conversation resources")} onCancel={() => setResourcePanelOpen(false)} onClick={(event) => { if (isDialogBackdropClick(event)) setResourcePanelOpen(false); }}>
            <header className="resource-header"><h2>{ui(language, "Conversation resources")}</h2><button className="icon-button" type="button" onClick={() => setResourcePanelOpen(false)} aria-label={ui(language, "Close resources")}><X size={18} /></button></header>
            <p>{conversationTitle(activeConversation.title)}</p><p className="resource-storage">{storageUsage}</p>
            <div className="resource-export-actions"><button type="button" onClick={() => void exportConversation("html")} disabled={backupBusy}><Download size={15} />{ui(language, "Export offline HTML with resources")}</button><button type="button" onClick={() => void exportConversation()} disabled={backupBusy}>Markdown</button></div>
            {resourceCache.busy ? <p role="status">{ui(language, "Saving resources to this device…")}</p> : null}
            {resourceCache.failed ? <div className="attachment-failure"><span>{ui(language, "{0} resources could not be saved. Temporary links may expire.", resourceCache.failed)}</span><button type="button" onClick={resourceCache.retry} disabled={resourceCache.busy}>{ui(language, "Retry saving")}</button></div> : null}
            {workspaceNotice ? <p role="status">{workspaceNotice}</p> : null}
            {!activeConversation.messages.some((m) => m.attachments?.length || m.generatedImages?.length || m.generatedFiles?.length || m.browserScreenshots?.length) ? <p className="history-empty">{ui(language, "This conversation has no attachments or generated resources yet.")}</p> : null}
            {activeConversation.messages.filter((m) => m.attachments?.length || m.generatedImages?.length || m.generatedFiles?.length || m.browserScreenshots?.length).map((message) => <section className="conversation-resources" key={message.id}>
              <time>{formatMessageTimestamp(message.createdAt, language)}</time>
              {message.attachments?.length ? <AttachmentStrip attachments={message.attachments} language={language} /> : null}
              <GeneratedImageGallery images={message.generatedImages} messageCreatedAt={message.createdAt} language={language} />
              <BrowserScreenshotGallery screenshots={message.browserScreenshots} messageCreatedAt={message.createdAt} language={language} />
              <GeneratedFileGallery files={message.generatedFiles} language={language} />
            </section>)}
          </dialog>

          <dialog ref={inspectorRef} className="inspector" aria-label={t.settings}
            onCancel={() => setInspectorOpen(false)}
            onClick={(event) => { if (isDialogBackdropClick(event)) setInspectorOpen(false); }}>
            <div className="inspector-header">
              <div><h2>{t.settings}</h2></div>
              <button className="icon-button" onClick={() => setInspectorOpen(false)} aria-label={t.closeInspector}><X size={17} /></button>
            </div>
            <div className="settings-tabs" aria-label={t.settings}>
              {(["general", "media", "account"] as const).map((tab) => (
                <button key={tab} type="button" aria-pressed={settingsTab === tab}
                  className={settingsTab === tab ? "active" : ""} onClick={() => setSettingsTab(tab)}>
                  {tab === "general" ? t.settingsGeneral : tab === "media" ? t.settingsMedia : t.settingsAccount}
                </button>
              ))}
            </div>
            <div hidden={settingsTab !== "general"}>
            <div className="settings-preferences"><LanguagePicker language={language} onChange={setLanguage} /><button type="button" onClick={() => setTheme((value) => value === "light" ? "dark" : "light")}>{theme === "light" ? <Moon size={16} /> : <Sun size={16} />}{t.toggleTheme}</button></div>
            <button className="settings-model" type="button" onClick={() => setModelPickerOpen(true)}>
              <span className="provider-mark"><ProviderLogo provider={activeModel.provider} /></span>
              <span><strong>{activeModel.name}</strong><small>{activeModel.provider}</small></span>
              <ChevronDown size={16} />
            </button>
            <p className="inspector-description">{getModelDescription(activeModel, language)}</p>
            <div className="inspector-stats">
              <div><span>{t.context}</span><strong>{formatContextWindow(activeModel.contextWindow, language, false)}</strong></div>
              <div><span>{t.inputPerMillion}</span><strong>{formatPrice(activeModel.prices.input)}</strong></div>
              <div><span>{t.outputPerMillion}</span><strong>{formatPrice(activeModel.prices.output)}</strong></div>
            </div>
            </div>
            <div hidden={settingsTab !== "account"}>
            <section className="workspace-backup-section">
              <h3>{t.localFirst}</h3><p>{t.localStorageHelp}</p>
              <p>{storageUsage}</p><p>{ui(language, "Generated images and files are automatically saved to this device.")}</p>
              <button className="storage-clean-button" type="button" onClick={() => void cleanLocalStorage()} disabled={storageState === "conflict" || storageState === "load-error"}>{ui(language, "Clean unused resources")}</button>
              <div className="workspace-backup-actions">
                <button type="button" onClick={() => void downloadBackup()} disabled={backupBusy || !hydrated}><Download size={15} />{backupBusy ? t.backupBusy : t.backupWorkspace}</button>
                <button type="button" onClick={() => importInputRef.current?.click()} disabled={backupBusy || generating || !hydrated || storageState === "load-error" || storageState === "conflict"}><Upload size={15} />{t.importWorkspace}</button>
              </div>
              <input ref={importInputRef} type="file" accept=".json,application/json" hidden onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void restoreBackup(file); }} />
              {workspaceNotice ? <p role="status">{workspaceNotice}</p> : null}
            </section>
            <div className="inspector-section cloudflare-account-section">
              <span className="section-title"><Cloud size={15} />{t.cloudflareAccount}</span>
              <div className={cloudflareAuth.authenticated ? "cloudflare-account-card connected" : "cloudflare-account-card"}>
                <div className="cloudflare-account-heading">
                  <span className="cloudflare-mark"><Cloud size={18} /></span>
                  <div>
                    <strong>{cloudflareAuth.authenticated && !cloudflareAuth.usesSiteQuota ? cloudflareAuth.activeAccountName : siteQuotaLabel}</strong>
                    <span>{cloudflareAuth.authenticated
                      ? cloudflareAuth.usesSiteQuota ? t.cloudflareAdminSiteQuota : t.cloudflareConnected
                      : siteQuotaDescription}</span>
                  </div>
                </div>
                {cloudflareAuth.authenticated ? (
                  <>
                    {cloudflareAuth.accounts.length > 1 && !cloudflareAuth.usesSiteQuota ? (
                      <label className="cloudflare-account-select">
                        <span>{t.cloudflareSelectAccount}</span>
                        <select
                          value={cloudflareAuth.activeAccountId}
                          disabled={cloudflareAuthBusy}
                          onChange={(event) => void changeCloudflareAccount(event.target.value)}
                        >
                          {cloudflareAuth.accounts.map((account) => (
                            <option key={account.id} value={account.id}>{account.name}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    <button className="cloudflare-disconnect" type="button" disabled={cloudflareAuthBusy} onClick={() => void logoutCloudflare()}>
                      {cloudflareAuthBusy ? <LoaderCircle className="spinning" size={14} /> : <LogOut size={14} />}
                      {t.cloudflareDisconnect}
                    </button>
                  </>
                ) : (
                  <button className="cloudflare-connect" type="button" disabled={cloudflareAuthBusy || !cloudflareAuth.configured} onClick={connectCloudflare}>
                    {cloudflareAuthBusy ? <LoaderCircle className="spinning" size={15} /> : <Cloud size={15} />}
                    {cloudflareAuthBusy ? t.cloudflareConnecting : cloudflareAuth.configured ? t.cloudflareConnect : t.cloudflareUnavailable}
                  </button>
                )}
              </div>
              {cloudflareAuthError ? <p className="cloudflare-auth-error" role="alert">{cloudflareAuthError}</p> : null}
              <p className="cloudflare-auth-privacy">{t.cloudflareAccountPrivacy}</p>
            </div>
            <div className="inspector-section pwa-section">
              <span className="section-title"><Bell size={15} />{t.pwaTitle}</span>
              <p>{t.pwaDescription}</p>
              <div className="pwa-actions">
                <button
                  type="button"
                  disabled={pwaInstalled || !installPrompt}
                  onClick={() => void installPwa()}
                >
                  <Download size={15} />
                  {pwaInstalled ? t.pwaInstalled : installPrompt ? t.pwaInstall : t.pwaInstallUnavailable}
                </button>
                <button
                  type="button"
                  className={notificationState === "enabled" ? "enabled" : ""}
                  disabled={notificationState === "unsupported" || notificationState === "denied" || notificationState === "enabled"}
                  onClick={() => void enableNotifications()}
                >
                  <Bell size={15} />
                  {notificationState === "enabled"
                    ? t.notificationsEnabled
                    : notificationState === "denied"
                      ? t.notificationsDenied
                      : notificationState === "unsupported"
                        ? t.notificationsUnsupported
                        : t.notificationsEnable}
                </button>
              </div>
            </div>
            </div>
            <div hidden={settingsTab !== "general"}>
            <div className="inspector-section">
              <label htmlFor="system-prompt"><Settings2 size={14} />{t.customInstructions}</label>
              <textarea
                id="system-prompt"
                name="system-prompt"
                rows={4}
                placeholder={t.customInstructionsPlaceholder}
                value={isBuiltInDefaultSystemPrompt(activeConversation.systemPrompt) ? "" : activeConversation.systemPrompt}
                onChange={(event) => updateConversation(activeConversation.id, (conversation) => ({
                  ...conversation,
                  systemPrompt: event.target.value.trim() ? event.target.value : t.defaultSystemPrompt,
                  updatedAt: now(),
                }))}
                aria-autocomplete="none"
                autoComplete="off"
                data-form-type="other"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
              />
            </div>
            <div className="inspector-section">
              <div className="range-label"><label htmlFor="temperature">{t.temperature}</label><output>{activeConversation.temperature.toFixed(1)}</output></div>
              <input
                id="temperature"
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={activeConversation.temperature}
                onChange={(event) => updateConversation(activeConversation.id, (conversation) => ({ ...conversation, temperature: Number(event.target.value), updatedAt: now() }))}
              />
              <div className="range-hints"><span>{t.precise}</span><span>{t.creative}</span></div>
            </div>
            <div className="inspector-section">
              <label htmlFor="max-tokens">{t.maxOutputTokens}</label>
              <input
                id="max-tokens"
                className="number-input"
                type="number"
                min="64"
                max={activeOutputTokenPolicy.maximum}
                step="256"
                value={activeConversation.maxTokens}
                onChange={(event) => updateConversation(activeConversation.id, (conversation) => ({
                  ...conversation,
                  maxTokens: clampOutputTokens(Number(event.target.value), activeOutputTokenPolicy),
                  maxTokensCustomized: true,
                  updatedAt: now(),
                }))}
              />
              <div className="number-hint">
                <span>{t.outputTokenRecommendation(
                  activeOutputTokenPolicy.recommended.toLocaleString(localeFor(language)),
                  activeOutputTokenPolicy.maximum.toLocaleString(localeFor(language)),
                )}</span>
                <button
                  type="button"
                  onClick={() => updateConversation(activeConversation.id, (conversation) => ({
                    ...conversation,
                    maxTokens: activeOutputTokenPolicy.recommended,
                    maxTokensCustomized: false,
                    updatedAt: now(),
                  }))}
                >{t.resetOutputTokens}</button>
              </div>
            </div>
            </div>
            <div hidden={settingsTab !== "media"}>
            <div className="inspector-section speech-model-section">
              <span className="section-title"><AudioLines />{t.speechModel}</span>
              <p>{t.speechModelDescription}</p>
              <div className="speech-model-options" role="radiogroup" aria-label={t.speechModel}>
                <button
                  className={speechMode === "quality" ? "speech-model-option selected" : "speech-model-option"}
                  type="button"
                  role="radio"
                  aria-checked={speechMode === "quality"}
                  onClick={() => setSpeechMode("quality")}
                >
                  <span className="speech-provider-mark aura"><AudioLines /></span>
                  <span className="speech-model-copy">
                    <strong>{t.speechQuality}<em>{t.speechDefault}</em></strong>
                    <small>{t.speechQualitySummary}</small>
                    <i>{t.speechQualityPrice}</i>
                  </span>
                  <span className="speech-model-check">{speechMode === "quality" ? <Check size={14} /> : null}</span>
                </button>
                <button
                  className={speechMode === "device" ? "speech-model-option selected" : "speech-model-option"}
                  type="button"
                  role="radio"
                  aria-checked={speechMode === "device"}
                  onClick={() => setSpeechMode("device")}
                >
                  <span className="speech-provider-mark system"><Volume2 /></span>
                  <span className="speech-model-copy">
                    <strong>{t.speechDevice}</strong>
                    <small>{t.speechDeviceSummary}</small>
                    <i>{t.speechDevicePrice}</i>
                  </span>
                  <span className="speech-model-check">{speechMode === "device" ? <Check size={14} /> : null}</span>
                </button>
              </div>
            </div>
            {activeModelSupportsTools ? (
              <div className="inspector-section image-model-section">
                <span className="section-title"><CreationGlyph />{t.imageModel}</span>
                <p>{t.imageModelDescription}</p>
                <div className="image-model-options" role="radiogroup" aria-label={t.imageModel}>
                  {IMAGE_MODELS.map((imageModel) => {
                    const selected = activeConversation.imageModelId === imageModel.id;
                    return (
                      <button
                        className={selected ? "image-model-option selected" : "image-model-option"}
                        key={imageModel.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => updateConversation(activeConversation.id, (conversation) => ({
                          ...conversation,
                          imageModelId: imageModel.id,
                          updatedAt: now(),
                        }))}
                      >
                        <span className={`image-provider-mark ${imageModel.provider === "Leonardo" ? "leonardo" : "flux"}`}>
                          {imageModel.provider === "Leonardo" ? "L" : (
                            <svg aria-hidden="true" viewBox="0 0 24 24">
                              <path d="M0 20.7 12 2.5l12 18.2h-2.2L12 5.9 3.5 18.8h12.1l1.2 1.9Z" />
                              <path d="m8.1 16.7 2-3.1 2.1 3.1Zm10.1 4-5.6-8.7h2.1l5.7 8.7Z" />
                            </svg>
                          )}
                        </span>
                        <span className="image-model-copy">
                          <strong>{imageModel.name}{imageModel.id === DEFAULT_IMAGE_MODEL_ID ? <em>{t.imageModelDefault}</em> : null}</strong>
                          <small>{imageModel.summary[language]}</small>
                          <i>{imageModel.price[language]}</i>
                        </span>
                        <span className="image-model-check">{selected ? <Check size={14} /> : null}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            </div>
            <div hidden={settingsTab !== "general"}>
            <div className="inspector-section">
              <span className="section-title">{t.capabilities}</span>
              <div className="inspector-capabilities">
                {activeModel.capabilities.length ? activeModel.capabilities.map((capability) => <span key={capability}>{getCapabilityLabel(capability, language)}</span>) : <span>{t.text}</span>}
                {activeModel.lora && <span>LoRA</span>}
                {activeModel.paid && <span className="paid">{t.paid}</span>}
              </div>
            </div>
            <button className="change-model" onClick={() => setModelPickerOpen(true)} type="button">{t.browseAllModels(models.length)}<ChevronDown size={15} /></button>
            </div>
          </dialog>
        </div>
      </main>

      {modelPickerOpen && (
        <ModelPicker
          models={models}
          selectedId={activeConversation.modelId}
          favoriteIds={workspace.favoriteModelIds}
          recentModelIds={workspace.recentModelIds}
          syncedAt={catalogSyncedAt}
          language={language}
          onSelect={selectModel}
          onToggleFavorite={toggleFavorite}
          onClose={() => setModelPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default App;
